import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'

import { IconFavoritFill, TextSubHeading, Link, TextCaption1 } from 'blocket-ui'
import { mediaQueries } from '@blazar-plugins/device'

/**
 * Because the BUI link spreads all props to the <a> element
 * we omit it here so that we don't pass `isActive` to
 * the BUI Link
 */
const OmitIsActiveLink = ({ isActive, ...props }) => {
  return <Link {...props} />
}

OmitIsActiveLink.propTypes = {
  isActive: PropTypes.bool,
}

export const Wrapper = styled.div`
  position: relative;
  z-index: 4;
  padding: 16px;
  background: #fff;
  color: #363433;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  border-bottom: 0.5px solid rgb(225, 225, 225);

  &:last-child {
    border-bottom: 0;
  }

  @media ${mediaQueries.large} {
    padding: 16px;
    box-shadow: 0 1px 0 0 rgba(0, 0, 0, 0.25);
    border: 0;
    border-radius: 2px;
    margin: 0 0 4px 0;
  }
`

export const Article = styled.article`
  display: flex;
`

export const Content = styled.div`
  margin-left: 16px;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`

export const ContentTop = styled.div`
  display: flex;
  align-items: baseline;
  margin-bottom: 4px;
  color: #696766;
  font-size: 12px;

  @media ${mediaQueries.medium} {
    font-size: 15px;
  }
`

export const ImageWrapper = styled.div`
  position: relative;
  align-self: flex-start;
  flex-shrink: 0;
  width: 135px;
  height: 90px;
  border-radius: 4px;

  @media ${mediaQueries.medium} {
    width: 203px;
    height: 135px;
  }
`

export const TitleWrapper = styled.div`
  display: flex;
`

export const ExtraServiceEnabledText = styled(TextCaption1)`
  width: 60px;
  white-space: nowrap;
  color: rgb(105, 103, 102);
  margin-left: 3px;
  font-family: 'BlocketSans-Regular', sans-serif;
  @media ${mediaQueries.large} {
    font-size: 15px;
  }
`

export const ExtraServiceEnabledWrapper = styled.div`
  display: flex;
  align-items: center;
  svg {
    color: #6b6967;
  }
`

const StyledTitle = styled(TextSubHeading)`
  flex-grow: 1;
  max-height: 45px;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
  word-break: break-word;
  display: -webkit-box;
  hyphens: auto;
  line-height: 20px;
`

/* We'd like to use the blocket-ui NotificationBadge component but due to restrictions in how :visited state can be styled we can't
/* See https://hacks.mozilla.org/2010/03/privacy-related-changes-coming-to-css-vistited/ */
export const Notification = styled.div`
  padding: 0px 6px 0px 6px;
  color: #fffaf5;
  background-color: #4182c3;
  height: 20px;
  border-radius: 100px;
  font-size: 11px;
  line-height: 1.92;
  margin-right: 6px;
  flex-shrink: 0; /* Prevents circle from getting squashed when the link title is long */
`

const StyledTitleLink = styled(Link)`
  display: flex;
  color: #363433;
  font-family: BlocketSans-Medium;
  line-height: 20px;
  font-size: 15px;
  @media ${mediaQueries.medium} {
    font-size: 17px;
  }

  /* Let's hide it by using the same background and foreground color, see line 100 */
  &:visited ${Notification} {
    background-color: #fff;
  }

  /* create an element which spans the whole card that is clickable */
  /* https://inclusive-components.design/cards/ */
  &::after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
  }
`
export const SubjectContainer = styled.span`
  flex-grow: 1;
  ${StyledTitleLink}:hover & {
    color: #363433;
    text-decoration: underline;
  }

  ${StyledTitleLink}:active & {
    color: #363433;
  }

  ${StyledTitleLink}:visited & {
    color: #843659;
  }
`

export const Title = ({ children, to, onClick }) => {
  return (
    <StyledTitle tagName="h2">
      <StyledTitleLink onClick={onClick} to={to}>
        {children}
      </StyledTitleLink>
    </StyledTitle>
  )
}
Title.propTypes = {
  children: PropTypes.node,
  to: PropTypes.string,
  onClick: PropTypes.func, // temporary onclick for disabling the popovers once "seen"
}

export const BodyWrapper = styled.p`
  margin-top: 4px;
  font-family: 'BlocketSans-Regular', sans-serif;
  line-height: normal;
  color: #696766;
  font-size: 12px;
  padding-top: 0;
  padding-bottom: 0;
  overflow: hidden;
  flex-grow: 1;
  text-overflow: ellipsis;
  word-break: break-word;
  -webkit-line-clamp: 2;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  hyphens: auto;

  @media ${mediaQueries.medium} {
    font-size: 15px;
  }
`

export const BodyText = styled.span`
  font-size: 12px;
  line-height: normal;

  @media ${mediaQueries.medium} {
    font-size: 15px;
  }
`

export const SubjectWrapper = styled.div`
  display: flex;
`

export const SubjectSymbol = styled.div`
  flex-shrink: 0;
`

export const InfoWrapper = styled.p`
  line-height: 20px;
  padding-top: 0;
  padding-bottom: 0;
  overflow: hidden;
  flex-grow: 1;
  text-overflow: ellipsis;
  word-break: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  -webkit-hyphens: auto;
  -moz-hyphens: auto;
  -ms-hyphens: auto;
  hyphens: auto;
`

export const ParamsWrapper = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: row;
`

export const RemoveSavedAd = styled.span`
  pointer-events: auto;
  align-self: center;
`

export const LocationTimeWrapper = styled.div`
  display: flex;
  align-items: baseline;
  margin-bottom: 4px;
  color: #696766;
  font-size: 12px;

  @media ${mediaQueries.medium} {
    font-size: 15px;
  }
`

export const Time = styled.p`
  font-size: 11px;
  text-align: right;
  white-space: nowrap;

  @media ${mediaQueries.medium} {
    font-size: 15px;
  }
`

export const ToplistingLabel = styled.p`
  font-size: 11px;
  text-align: right;
  white-space: nowrap;

  @media ${mediaQueries.medium} {
    font-size: 15px;
  }
`

export const SalesInfo = styled.div`
  display: flex;
  justify-content: space-between;
`

export const HeartIcon = () => <IconFavoritFill width={24} height={24} color="#ef404f" />

export const ApplyDaysLeftLabel = styled.span`
  color: #8b8988;
  margin-right: 0.5em;
`

export const TopInfoWrapper = styled.p`
  line-height: 20px;
  padding-top: 0;
  padding-bottom: 0;
  overflow: hidden;
  flex-grow: 1;
  text-overflow: ellipsis;
  word-break: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow-wrap: anywhere;
`

export const StoreLogo = styled.img`
  max-width: 87px;
  max-height: 45px;
  object-fit: contain;

  @media ${mediaQueries.medium} {
    max-width: 130px;
    max-height: 60px;
  }
`
export const LinkWrapper = styled.p`
  margin-top: 4px;
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-size: 12px;
  color: #4182c3;
  font-weight: bold;

  @media ${mediaQueries.medium} {
    font-size: 15px;
  }
`
